/**
 * useActiveInstances — live + adaptive-polling view of running + paused
 * workflow instances for the Active tab and the AppShell tab badge.
 *
 * Per plan §"Active workflows list" + §"Self-recovery":
 *   * SignalR push (workflowEvent) is subscribed for each row's instance —
 *     status / current_phase / updated_at fold directly into row state, and
 *     terminal events drop the row from the list immediately.
 *   * Polling backstops push as a heartbeat to catch new instances starting
 *     (push doesn't fire for instances we don't yet subscribe to):
 *       - SignalR connected, visible tab → 30s heartbeat
 *       - SignalR unavailable, visible tab → 5s fast cadence
 *       - Hidden tab (either mode) → 5 min, plus an immediate refetch the
 *         moment the tab is visible again
 *   * `lastError` + `lastSuccessAt` exposed; `isStale` true when the last
 *     successful poll was more than 30s ago.
 *   * `refetch()` clears prior error and immediately re-runs the call.
 *
 * Returns `{ rows, refetch, count, lastError, lastSuccessAt, isStale }`.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { orchestratorApi } from "../services/orchestratorApi";
import type { ActiveWorkflow, WorkflowEvent } from "../types/workflow";
import { IN_PROGRESS_STATUSES } from "../types/workflow";
import { useSignalR, WORKFLOW_EVENT_METHOD } from "../signalr/useSignalR";
import { isTerminalDrawerStatus } from "@product-app/common/workflows/sectionOrder";
import { FINISHED_STATUSES } from "@product-app/common/workflows/warningAcknowledgement";
import { logger } from "../obs/logger";

export interface UseActiveInstancesOptions {
  /** Status filter sent to substrate. Defaults to running + paused_hitl. */
  statusFilter?: string[];
  /** Page size; defaults to 50. */
  limit?: number;
  /** Visible-tab interval used while SignalR is unavailable AND rows exist.
   *  Default 5000ms. */
  visibleIntervalMs?: number;
  /** Hidden-tab interval. Default 300000ms (5 min): nobody is reading a hidden
   *  pane, and becoming visible refetches at once. */
  hiddenIntervalMs?: number;
  /** Visible-tab heartbeat when SignalR is connected. Default 30000ms — push
   *  handles fast updates; this slow cadence only catches new instances
   *  starting. */
  heartbeatIntervalMs?: number;
  /** Visible-tab interval when there are no rows AND SignalR is reachable.
   *  Default 60000ms. There's nothing for SignalR to subscribe to in this
   *  state, so the only reason to poll is to detect a new instance
   *  starting; once-a-minute is enough. */
  idleIntervalMs?: number;
  /** Threshold beyond `lastSuccessAt` that flips `isStale` true. */
  staleAfterMs?: number;
  /** Disable polling — used in tests so the hook can be inspected without timers. */
  disabled?: boolean;
}

export interface UseActiveInstancesResult {
  rows: ActiveWorkflow[];
  refetch: () => Promise<void>;
  count: number;
  lastError: Error | null;
  lastSuccessAt: number | null;
  isStale: boolean;
  loading: boolean;
  /** True when at least one per-row SignalR connection is established. */
  isSignalRConnected: boolean;
}

const DEFAULT_FILTER: string[] = [...IN_PROGRESS_STATUSES];

/**
 * R88: the row's `needs_acknowledgement` after a push patched its status WITHOUT
 * a server read. A flag read while the run was still running says nothing about
 * the finished run's lines, so a CHANGE into a finished status is "needs
 * acknowledgement" (true: the pill reads "Completed*") until the next server read,
 * never a plain "Completed". A change to any other status is unknown (null). A
 * same-status repeat leaves the flag as it was.
 */
export function foldNeedsAcknowledgement(
  from: string | null | undefined,
  to: string | null | undefined,
  flag: boolean | null | undefined,
): boolean | null | undefined {
  if (!to || (from ?? "").toLowerCase() === to.toLowerCase()) return flag;
  return FINISHED_STATUSES.has(to.toLowerCase()) ? true : null;
}

export function useActiveInstances(
  options: UseActiveInstancesOptions = {},
): UseActiveInstancesResult {
  const {
    statusFilter,
    limit = 50,
    visibleIntervalMs = 5_000,
    hiddenIntervalMs = 300_000,
    heartbeatIntervalMs = 30_000,
    idleIntervalMs = 60_000,
    staleAfterMs = 30_000,
    disabled = false,
  } = options;

  const filterKey = useMemo(
    () => (statusFilter ?? DEFAULT_FILTER).join(","),
    [statusFilter],
  );

  // Poll result batched into a single state object so each fetch cycle
  // causes exactly one re-render on success (not three separate ones).
  type PollState = {
    rows: ActiveWorkflow[];
    lastError: Error | null;
    lastSuccessAt: number | null;
    loading: boolean;
  };
  type PollAction =
    | { type: "start" }
    | { type: "success"; rows: ActiveWorkflow[]; at: number }
    | { type: "error"; error: Error }
    | { type: "done" }
    // Fold a SignalR workflowEvent into the matching row (status / phase
    // / updated_at). No-op if the row isn't currently in the list.
    | { type: "push_fold"; instanceId: string; partial: Partial<ActiveWorkflow> }
    // Drop a row that just transitioned to a terminal status — substrate's
    // status_filter would also drop it on the next poll, but applying here
    // makes the UI react instantly to push events.
    | { type: "push_drop"; instanceId: string };

  const [pollState, dispatch] = useReducer(
    (s: PollState, a: PollAction): PollState => {
      switch (a.type) {
        case "start":   return { ...s, loading: true };
        case "success": return { ...s, rows: a.rows, lastSuccessAt: a.at, lastError: null, loading: false };
        case "error":   return { ...s, lastError: a.error, loading: false };
        case "done":    return { ...s, loading: false };
        case "push_fold": {
          let changed = false;
          const next = s.rows.map((r) => {
            if (r.instance_id !== a.instanceId) return r;
            changed = true;
            return {
              ...r,
              ...a.partial,
              needs_acknowledgement: foldNeedsAcknowledgement(
                r.status,
                a.partial.status,
                r.needs_acknowledgement,
              ),
            };
          });
          return changed ? { ...s, rows: next } : s;
        }
        case "push_drop": {
          const before = s.rows.length;
          const next = s.rows.filter((r) => r.instance_id !== a.instanceId);
          return next.length === before ? s : { ...s, rows: next };
        }
        default:        return s;
      }
    },
    { rows: [], lastError: null, lastSuccessAt: null, loading: false },
  );

  const [now, setNow] = useState<number>(Date.now());

  const mountedRef = useRef(true);
  const filterArrayRef = useRef<string[]>(statusFilter ?? DEFAULT_FILTER);
  filterArrayRef.current = statusFilter ?? DEFAULT_FILTER;
  // Tracks whether the polling effect has done its initial fetch — prevents
  // a redundant immediate fetch when the effect re-runs because of
  // signalR.isConnected / isSignalRUnavailable flips.
  const hasFetchedRef = useRef(false);

  // Single source of truth for the current poll. Wrapped so refetch can
  // also call it. `showLoading` is true only for the initial fetch so
  // background polling doesn't flash the loading state.
  const fetchOnce = useCallback(async (showLoading = false) => {
    if (showLoading) dispatch({ type: "start" });
    try {
      const result = await orchestratorApi.listInstances({
        status_filter: filterArrayRef.current,
        limit,
        // The In-flight list renders no metadata. Compact rows keep only the
        // oraFix campaign link the transport's campaign filter reads (2026-10-06).
        compact: true,
      });
      if (!mountedRef.current) return;
      dispatch({ type: "success", rows: result.items ?? [], at: Date.now() });
    } catch (err) {
      if (!mountedRef.current) return;
      const e = err instanceof Error ? err : new Error(String(err));
      dispatch({ type: "error", error: e });
      logger.warn("useActiveInstances: poll failed", { error: e.message });
    }
  }, [limit]);

  const refetch = useCallback(async () => {
    await fetchOnce(true);
  }, [fetchOnce]);

  // ── SignalR push: subscribe per active row ─────────────────────────
  // For each row currently in the list, open a SignalR channel and fold
  // workflowEvent updates in-place. Terminal events drop the row (substrate
  // would also drop it on the next poll; this is just faster). When a row
  // disappears from the list (poll superseded it) we tear down its sub.
  // SignalR with reconcile-on-reconnect: when the WS recovers from a
  // transient drop, the substrate doesn't replay the workflowEvents that
  // fired during the outage. A fresh `refetch()` of the list picks up any
  // new instances that started + any state changes on rows we already had.
  const signalR = useSignalR({
    onReconnected: (id) => {
      logger.info(
        "[useActiveInstances] SignalR reconnected — refetching to reconcile missed events",
        { instance_id: id },
      );
      void fetchOnce();
    },
    onConnected: ({ instanceId: id, connectionId, hub }) => {
      if (!connectionId) return;
      void orchestratorApi.joinSignalRGroup(id, connectionId, hub);
    },
  });
  const subscribedIdsRef = useRef<Set<string>>(new Set());
  const unsubscribesRef = useRef<Map<string, () => void>>(new Map());
  // Wall-clock timestamp of the last failed signalR.connect() per
  // instance_id. Subsequent retries are skipped until 60s have elapsed.
  // Without this, a new workflow whose negotiate transiently 4xxs (e.g.
  // substrate hasn't fully committed the row yet) would be reconnected
  // on every render, hammering the negotiate endpoint.
  const connectFailedAtRef = useRef<Map<string, number>>(new Map());
  const RETRY_BACKOFF_MS = 60_000;
  // Pin the signalR methods to a ref so effects can call them without taking
  // signalR (which is a new object every render) as a dependency. Using the
  // object identity in deps would re-run effects on every render → tear-down
  // and re-subscribe in a tight loop.
  const signalRRef = useRef(signalR);
  signalRRef.current = signalR;

  // Stable handler — dispatches into the reducer without re-running the
  // effect on every render. Using a ref so the subscription captures the
  // latest dispatch identity (which is itself stable from useReducer).
  const handleEventRef = useRef<(evt: WorkflowEvent) => void>(() => undefined);
  handleEventRef.current = (evt: WorkflowEvent) => {
    if (!evt || !evt.instance_id) return;
    const status = (evt.status ?? "").toLowerCase();
    if (isTerminalDrawerStatus(status)) {
      dispatch({ type: "push_drop", instanceId: evt.instance_id });
      return;
    }
    dispatch({
      type: "push_fold",
      instanceId: evt.instance_id,
      partial: {
        status: evt.status ?? undefined,
        current_phase: evt.current_phase ?? undefined,
        updated_at: evt.timestamp ?? undefined,
      } as Partial<ActiveWorkflow>,
    });
  };

  useEffect(() => {
    if (disabled) {
      logger.info("[useActiveInstances] SignalR skip — hook disabled");
      return;
    }
    if (signalR.isSignalRUnavailable) {
      logger.info(
        "[useActiveInstances] SignalR skip — substrate marked unavailable; polling at fast cadence",
      );
      return;
    }

    const desired = new Set(pollState.rows.map((r) => r.instance_id));
    const current = subscribedIdsRef.current;
    const now = Date.now();

    // Subscribe to new rows.
    desired.forEach((id) => {
      if (current.has(id)) return;
      const failedAt = connectFailedAtRef.current.get(id);
      if (failedAt !== undefined && now - failedAt < RETRY_BACKOFF_MS) {
        // Still inside the backoff window — let the next idle-cadence
        // poll re-trigger this effect after the timer elapses.
        return;
      }
      current.add(id);
      connectFailedAtRef.current.delete(id);
      logger.info("[useActiveInstances] SignalR connect: starting", { instance_id: id });
      void (async () => {
        try {
          await signalRRef.current.connect(id);
        } catch (err) {
          logger.warn("[useActiveInstances] SignalR connect: failed; backing off", {
            instance_id: id,
            backoff_ms: RETRY_BACKOFF_MS,
            error: err instanceof Error ? err.message : String(err),
          });
          subscribedIdsRef.current.delete(id);
          connectFailedAtRef.current.set(id, Date.now());
          return;
        }
        if (!subscribedIdsRef.current.has(id)) return; // unmounted before connect resolved
        logger.info("[useActiveInstances] SignalR connect: succeeded", { instance_id: id });
        const off = signalRRef.current.subscribe<WorkflowEvent>(WORKFLOW_EVENT_METHOD, (evt) => {
          if (evt?.instance_id !== id) return;
          handleEventRef.current(evt);
        });
        unsubscribesRef.current.set(id, off);
      })();
    });

    // Tear down rows that fell off the list.
    Array.from(current).forEach((id) => {
      if (desired.has(id)) return;
      current.delete(id);
      connectFailedAtRef.current.delete(id);
      const off = unsubscribesRef.current.get(id);
      try {
        off?.();
      } catch {
        // best-effort
      }
      unsubscribesRef.current.delete(id);
      logger.info("[useActiveInstances] SignalR disconnect: row removed", { instance_id: id });
      void signalRRef.current.disconnect(id);
    });
    // signalR identity is unstable per render — use signalRRef inside.
    // Booleans go in deps so the effect re-evaluates when push availability
    // changes (so we tear down on unavailable, retry when it recovers).
  }, [pollState.rows, disabled, signalR.isSignalRUnavailable]);

  // Cleanup on unmount — disconnect every active subscription.
  useEffect(() => {
    return () => {
      const ids = Array.from(subscribedIdsRef.current);
      subscribedIdsRef.current.clear();
      ids.forEach((id) => {
        const off = unsubscribesRef.current.get(id);
        try {
          off?.();
        } catch {
          // best-effort
        }
        void signalRRef.current.disconnect(id);
      });
      unsubscribesRef.current.clear();
    };
    // signalR identity is stable enough for unmount cleanup; we don't want
    // this effect re-running on every signalR-state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adaptive cadence + visibility tracking + auto-refetch on visibility-resume.
  // Filter changes reset the hasFetchedRef so the new filter triggers a
  // fresh initial fetch.
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [filterKey]);

  useEffect(() => {
    mountedRef.current = true;
    if (disabled) return () => {
      mountedRef.current = false;
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const isHidden = (): boolean => {
      if (
        typeof document !== "undefined" &&
        typeof document.visibilityState === "string"
      ) {
        return document.visibilityState === "hidden";
      }
      return false;
    };

    const cadence = (): number => {
      if (isHidden()) {
        return hiddenIntervalMs;
      }
      // SignalR is the source of truth — push handles per-row updates and
      // a slow heartbeat is enough to catch new instances starting.
      if (signalR.isConnected && !signalR.isSignalRUnavailable) {
        return heartbeatIntervalMs;
      }
      // SignalR is genuinely unavailable (sticky flag set after a 503 on
      // negotiate or repeated connect failures). Polling is the only feed,
      // so run it fast.
      if (signalR.isSignalRUnavailable) {
        return visibleIntervalMs;
      }
      // SignalR is reachable but not currently connected — either there
      // are no rows to subscribe to, or connect is transiently failing
      // (e.g. brand-new instance not yet authorized by the substrate, or
      // mid-handshake). Either way, hammering the list endpoint at 5s
      // doesn't help: the next per-row connect retry runs at the same
      // idle cadence (see retryBackoffMs). Slow polling here.
      return idleIntervalMs;
    };

    const cadenceReason = (): string => {
      if (isHidden()) return "tab hidden";
      if (signalR.isConnected && !signalR.isSignalRUnavailable) {
        return "SignalR connected — heartbeat only";
      }
      if (signalR.isSignalRUnavailable) return "SignalR unavailable — fast polling";
      if (pollState.rows.length === 0) {
        return "no rows — idle cadence (will subscribe via SignalR when one appears)";
      }
      return "SignalR connecting / retrying — idle cadence";
    };

    const schedule = () => {
      if (cancelled) return;
      const ms = cadence();
      logger.info("[useActiveInstances] poll scheduled", {
        in_ms: ms,
        reason: cadenceReason(),
        signalr_connected: signalR.isConnected,
        signalr_unavailable: signalR.isSignalRUnavailable,
        rows: pollState.rows.length,
      });
      timer = setTimeout(async () => {
        await fetchOnce();
        schedule();
      }, ms);
    };

    // Fire immediately on mount with loading indicator. On subsequent
    // re-runs (e.g. SignalR flipping isConnected) we only reschedule —
    // re-fetching here would cause a redundant list call every time
    // SignalR's state changed.
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void fetchOnce(true);
    }
    schedule();

    const onVisibility = () => {
      // Resume immediately on visible — clear any pending hidden-cadence
      // timer and refetch right now.
      if (!isHidden()) {
        if (timer) clearTimeout(timer);
        void fetchOnce();
        schedule();
      } else {
        // Hidden — let the current timer expire naturally; subsequent
        // schedule() calls pick the slow cadence.
        if (timer) {
          clearTimeout(timer);
          schedule();
        }
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (timer) clearTimeout(timer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // filterKey forces re-init on filter change. signalR flags re-init the
    // loop so the cadence picks up the connected/unavailable state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterKey,
    visibleIntervalMs,
    hiddenIntervalMs,
    heartbeatIntervalMs,
    idleIntervalMs,
    disabled,
    fetchOnce,
    signalR.isConnected,
    signalR.isSignalRUnavailable,
    // pollState.rows.length flips into the cadence (idle vs fast) — re-run
    // so a row appearing or disappearing immediately picks the right ms.
    pollState.rows.length,
  ]);

  // Tick `now` every 5s so isStale recalculates without forcing refetches.
  useEffect(() => {
    if (disabled) return;
    const id = setInterval(() => {
      if (mountedRef.current) setNow(Date.now());
    }, 5_000);
    return () => clearInterval(id);
  }, [disabled]);

  const isStale = useMemo(() => {
    if (pollState.lastSuccessAt === null) return false;
    return now - pollState.lastSuccessAt > staleAfterMs;
  }, [now, pollState.lastSuccessAt, staleAfterMs]);

  return {
    rows: pollState.rows,
    refetch,
    count: pollState.rows.length,
    lastError: pollState.lastError,
    lastSuccessAt: pollState.lastSuccessAt,
    isStale,
    loading: pollState.loading,
    isSignalRConnected: signalR.isConnected,
  };
}

export default useActiveInstances;
